import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useSession } from "@/app/session";
import { Shell } from "@/components/Shell";
import { Alert } from "@/components/ui";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

interface UserRow {
  id: string;
  name: string;
  email: string;
  role: string;
  since: string;
  lastActive: string | null;
  activeMinutes30: number;
  activeMinutes7: number;
  activeDays30: number;
  boardsOpened30: number;
  boardOpens30: number;
  cardsCreated30: number;
  comments30: number;
  uploads30: number;
  searches30: number;
  exports30: number;
  other30: number;
  daily: Record<string, number>;
}
interface Recent {
  id: string;
  userName: string;
  kind: string;
  boardTitle: string | null;
  boardId: string | null;
  detail: string | null;
  createdAt: string;
}
interface Summary {
  enabled: boolean;
  users: UserRow[];
  recent: Recent[];
}

const fmtMin = (m: number) => (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`);
const ago = (iso: string | null, t: ReturnType<typeof useT>) => {
  if (!iso) return t.usage.never;
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return t.usage.minutesAgo(Math.max(1, Math.round(s / 60)));
  if (s < 86400) return t.usage.hoursAgo(Math.round(s / 3600));
  return t.usage.daysAgo(Math.round(s / 86400));
};

/** Owners/admins: who is using the app, how much, and what they do (last 30 days). */
export default function Usage() {
  const t = useT();
  const { me } = useSession();
  const [data, setData] = useState<Summary | null>(null);
  useEffect(() => {
    api<Summary>("/api/usage/summary").then(setData);
  }, []);
  if (me && me.staffRole !== "owner" && me.staffRole !== "admin") return null;
  const days = [...Array(14)].map((_, i) => {
    const d = new Date(Date.now() - (13 - i) * 864e5);
    return d.toISOString().slice(0, 10);
  });
  return (
    <Shell>
      <div className="mx-auto max-w-6xl px-6 py-8">
        <h1 className="display mb-2 text-4xl">.{t.usage.title}</h1>
        <p className="mb-6 text-sm text-muted">{t.usage.hint}</p>
        {data && !data.enabled ? (
          <div className="mb-4">
            <Alert>{t.usage.off}</Alert>
          </div>
        ) : null}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-dim">
              <tr>
                <th className="py-2 pr-3">{t.usage.user}</th>
                <th className="py-2 pr-3">{t.usage.lastActive}</th>
                <th className="py-2 pr-3">{t.usage.active7}</th>
                <th className="py-2 pr-3">{t.usage.active30}</th>
                <th className="py-2 pr-3">{t.usage.days30}</th>
                <th className="py-2 pr-3">{t.usage.boards}</th>
                <th className="py-2 pr-3">{t.usage.cards}</th>
                <th className="py-2 pr-3">{t.usage.comments}</th>
                <th className="py-2 pr-3">{t.usage.uploads}</th>
                <th className="py-2 pr-3">{t.usage.searches}</th>
                <th className="py-2 pr-3">{t.usage.exports}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data?.users.map((u) => (
                <tr key={u.id} className={u.activeMinutes30 === 0 ? "text-dim" : ""}>
                  <td className="py-2 pr-3">
                    <div className="font-semibold text-text">{u.name}</div>
                    <div className="text-xs text-dim">
                      {u.email} · {t.roles[u.role] ?? u.role}
                    </div>
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">{ago(u.lastActive, t)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{fmtMin(u.activeMinutes7)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{fmtMin(u.activeMinutes30)}</td>
                  <td className="py-2 pr-3">{u.activeDays30}</td>
                  <td className="py-2 pr-3" title={t.usage.boardsTitle(u.boardOpens30)}>
                    {u.boardsOpened30}
                  </td>
                  <td className="py-2 pr-3">{u.cardsCreated30}</td>
                  <td className="py-2 pr-3">{u.comments30}</td>
                  <td className="py-2 pr-3">{u.uploads30}</td>
                  <td className="py-2 pr-3">{u.searches30}</td>
                  <td className="py-2 pr-3">{u.exports30}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="display mt-10 mb-3 border-b border-border pb-2 text-2xl">.{t.usage.dailyTitle}</h2>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead className="text-dim">
              <tr>
                <th className="py-1 pr-3 text-left">{t.usage.user}</th>
                {days.map((d) => (
                  <th key={d} className="px-1 py-1 text-center font-normal">
                    {d.slice(8)}/{d.slice(5, 7)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.users
                .filter((u) => u.activeMinutes30 > 0)
                .map((u) => (
                  <tr key={u.id}>
                    <td className="py-1 pr-3 whitespace-nowrap">{u.name}</td>
                    {days.map((d) => {
                      const m = u.daily[d] ?? 0;
                      return (
                        <td key={d} className="px-1 py-1 text-center">
                          <span
                            className="inline-block min-w-8 rounded-sm px-1"
                            style={{
                              background: m ? `rgba(233,197,106,${Math.min(1, 0.15 + m / 120)})` : undefined,
                            }}
                            title={`${d}: ${fmtMin(m)}`}
                          >
                            {m || "·"}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <h2 className="display mt-10 mb-3 border-b border-border pb-2 text-2xl">.{t.usage.recentTitle}</h2>
        <ul className="divide-y divide-border text-sm">
          {data?.recent.length === 0 ? <li className="py-2 text-dim">—</li> : null}
          {data?.recent.map((r) => (
            <li key={r.id} className="flex flex-wrap items-baseline gap-x-3 py-1.5">
              <span className="w-32 shrink-0 text-xs text-dim">
                {new Date(r.createdAt).toLocaleString("es-MX")}
              </span>
              <span className="font-semibold">{r.userName}</span>
              <span className="text-muted">{t.usage.kinds[r.kind] ?? r.kind}</span>
              {r.detail ? <span className="text-dim">{r.detail}</span> : null}
              {r.boardId && r.boardTitle ? (
                <Link to={`/b/${r.boardId}`} className="text-muted hover:text-text">
                  {r.boardTitle}
                </Link>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Shell>
  );
}
